import { Link, createFileRoute } from "@tanstack/react-router";
import { FileText } from "lucide-react";

import { PRODUCT_HOME } from "@/lib/product";

export const Route = createFileRoute("/terms-and-conditions")({
  head: () => ({ meta: [{ title: "Terms and Conditions · GENRL Wallet" }] }),
  component: TermsAndConditionsPage,
});

function TermsAndConditionsPage() {
  return (
    <main className="wallet-ops-shell min-h-screen">
      <div className="wallet-ops-container max-w-4xl space-y-6 py-8">
        <header className="wallet-ops-panel p-6">
          <div className="flex items-start gap-3">
            <div className="wallet-ops-metric-icon">
              <FileText className="h-4 w-4" />
            </div>
            <div>
              <p className="wallet-ops-label">Legal</p>
              <h1 className="mt-2 text-[30px] font-semibold tracking-normal text-[var(--wallet-ops-fg)]">Terms and Conditions</h1>
              <p className="mt-2 text-[13px] leading-6 text-[var(--wallet-ops-subtle)]">
                These terms describe the demand commitment flow for pooled compute. They are draft product terms and should be reviewed by legal counsel before production use.
              </p>
            </div>
          </div>
        </header>

        <section className="wallet-ops-panel divide-y divide-[var(--wallet-ops-border)]">
          <TermsSection title="Demand Commitments">
            By submitting a pooled compute demand commitment, you confirm that you are authorised to act for the relevant organisation and to submit demand for the selected provider, hardware, GPU quantity, and duration.
          </TermsSection>
          <TermsSection title="Contract Offer">
            A submitted demand commitment is used to source or negotiate matching compute capacity. If a matching contract offer is presented and accepted under the applicable contracting process, you may be legally required to satisfy the committed quantities, duration, payment obligations, operational requirements, and any other contract terms.
          </TermsSection>
          <TermsSection title="Accuracy">
            You are responsible for ensuring that submitted demand details are accurate, including provider selection, hardware type, GPU count, duration, billing entity, and any supporting commercial information.
          </TermsSection>
          <TermsSection title="Provider Negotiation">
            You agree that submitted demand may be shared or used in aggregated form to negotiate supply, price, availability, and commercial terms with compute providers.
          </TermsSection>
          <TermsSection title="No Final Contract Until Offered">
            Submitting demand records your intent and willingness to contract for matching supply. It does not by itself guarantee capacity, price, availability, or final contract execution unless and until a formal offer and acceptance process is completed.
          </TermsSection>
        </section>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to="/privacy" className="text-[12px] text-[var(--wallet-ops-link)] underline underline-offset-4">
            Privacy Policy
          </Link>
          <Link to={PRODUCT_HOME} className="wallet-ops-button min-h-[42px] px-4">
            Back to app
          </Link>
        </div>
      </div>
    </main>
  );
}

function TermsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="p-5">
      <h2 className="wallet-ops-label text-[12px]">{title}</h2>
      <p className="mt-3 text-[14px] leading-7 text-[var(--wallet-ops-subtle)]">{children}</p>
    </section>
  );
}
